import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import { TextField } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const DIVIDER = 'var(--vo-cloud)';
const BORDER_IDLE = 'var(--vo-steel)';

const SelectorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const ColLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 10px;
`;

const SearchWrap = styled.div`
  position: relative;
  margin-bottom: 12px;
`;
const AvailList = styled.div`
  border: 1px solid ${DIVIDER};
  border-radius: 10px;
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
`;
const AvailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--vo-mist);
  cursor: pointer;
  background: ${({ $sel }) => $sel ? 'var(--vo-capri-50)' : 'white'};
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const AvailInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;
const AvailNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const AvailName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
`;
const AvailBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: ${MUTED};
  background: var(--vo-mist);
  padding: 1px 7px;
  border-radius: 8px;
`;
const AvailSub = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const CheckCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $sel }) => $sel ? BLUE : 'transparent'};
  border: ${({ $sel }) => $sel ? 'none' : `1.5px solid var(--vo-line)`};
`;

const SelPanel = styled.div`
  border: 1.5px solid ${({ $hasItems }) => $hasItems ? BLUE : 'var(--vo-line)'};
  border-radius: 10px;
  overflow: hidden;
  min-height: 120px;
  background: var(--vo-capri-25);
`;
const SelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${DIVIDER};
  &:last-child { border-bottom: none; }
`;
const SelInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const SelName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const SelSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const RemoveBtn = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${MUTED};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover { color: var(--vo-danger); }
`;
const EmptyPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

const InfoBanner = styled.div`
  background: var(--vo-capri-50);
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const InfoText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${BLUE};
  margin: 0;
`;
const EmptyAvail = styled.div`
  padding: 14px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

function FrameworkSelector({ frameworks, selectedFrameworks, handleToggle }) {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFrameworks = frameworks.filter(fw =>
    fw.sourceIdentifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fw.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalControls = selectedFrameworks.reduce((sum, fw) => sum + (fw.controlCount ?? 0), 0);

  return (
    <SelectorGrid>
      {/* ── Left: available ── */}
      <div>
        <ColLabel>{t('gap-analysis:form.frameworks.availableLabel')}</ColLabel>
        <SearchWrap>
          <TextField
            fullWidth
            placeholder={t('gap-analysis:form.frameworks.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{ '& .MuiOutlinedInput-input.MuiOutlinedInput-input': { paddingLeft: '42px', paddingRight: '16px' } }}
          />
          <svg
            width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke={BORDER_IDLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          >
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </SearchWrap>
        <AvailList>
          {filteredFrameworks.length > 0 ? filteredFrameworks.map(fw => {
            const isSel = selectedFrameworks.some(s => s._id === fw._id);
            return (
              <AvailRow key={fw._id} $sel={isSel} onClick={() => handleToggle(fw)}>
                <AvailInfo>
                  <AvailNameRow>
                    <AvailName>{fw.sourceIdentifier}</AvailName>
                    {fw.category && <AvailBadge>{fw.category}</AvailBadge>}
                  </AvailNameRow>
                  <AvailSub>
                    {t('gap-analysis:form.frameworks.controlsCount', { count: fw.controlCount ?? 0 })}
                  </AvailSub>
                </AvailInfo>
                <CheckCircle $sel={isSel}>
                  {isSel && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </CheckCircle>
              </AvailRow>
            );
          }) : (
            <EmptyAvail>{t('gap-analysis:form.frameworks.notFound')}</EmptyAvail>
          )}
        </AvailList>
      </div>

      {/* ── Right: selected ── */}
      <div>
        <ColLabel>
          {t('gap-analysis:form.frameworks.selectedWithCount', { count: selectedFrameworks.length })}
        </ColLabel>
        <SelPanel $hasItems={selectedFrameworks.length > 0}>
          {selectedFrameworks.length > 0 ? (
            selectedFrameworks.map(fw => (
              <SelRow key={fw._id}>
                <SelInfo>
                  <SelName>{fw.sourceIdentifier}</SelName>
                  <SelSub>
                    {fw.controlCount ?? 0}{' '}
                    {t('gap-analysis:form.frameworks.controlsLabel')}
                    {fw.category ? ` · ${fw.category}` : ''}
                  </SelSub>
                </SelInfo>
                <RemoveBtn type="button" onClick={() => handleToggle(fw)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </RemoveBtn>
              </SelRow>
            ))
          ) : (
            <EmptyPanel>{t('gap-analysis:form.frameworks.noneSelected')}</EmptyPanel>
          )}
        </SelPanel>
        {selectedFrameworks.length > 0 && (
          <InfoBanner>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <InfoText>
              <Trans
                i18nKey="gap-analysis:form.frameworks.totalBanner"
                values={{ controls: totalControls, count: selectedFrameworks.length }}
                components={{ b: <strong /> }}
              />
            </InfoText>
          </InfoBanner>
        )}
      </div>
    </SelectorGrid>
  );
}

export default FrameworkSelector;
